import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { PageHead, EmptyState } from "@/components/storefront/common";
import { directionsUrl } from "@/lib/format";
import { resolveMediaUrl, handleMediaError } from "@/lib/media";
import {
  Smartphone, Wrench, MessageCircle, Wand2, Navigation, Phone, Mail, Store, X, ArrowRight, Clock, IndianRupee,
} from "lucide-react";

const ICONS = { smartphone: Smartphone, wrench: Wrench, "message-circle": MessageCircle, wand: Wand2 };

export default function Services() {
  const { business } = useStore();
  const b = business || {};
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    document.title = b.name ? `Services — ${b.name}` : "Services";
    api.get("/services").then(({ data }) => setServices(data.services)).finally(() => setLoading(false));
  }, [b.name]);

  return (
    <div className="container-x py-8">
      <PageHead title="Store Services" crumbs={[{ label: "Home", to: "/" }, { label: "Services" }]} />
      <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 mb-8 flex items-start gap-3" data-testid="services-notice">
        <Store className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-sm text-amber-900">These services are available at our physical store. Please visit the store or contact us for availability and pricing. Services are not purchasable online.</p>
      </div>

      {!loading && services.length === 0 ? (
        <EmptyState icon={Wrench} title="Services coming soon" text="We're updating our in-store services. Please contact us for assistance." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s) => {
            const Icon = ICONS[s.icon] || Wrench;
            return (
              <button key={s.id} onClick={() => setActive(s)} data-testid={`service-card-${s.slug}`}
                className="text-left rounded-xl border border-slate-200 bg-white hover:shadow-lg hover:border-amber-300 transition overflow-hidden group">
                {s.image ? (
                  <div className="aspect-video bg-slate-50 overflow-hidden"><img src={resolveMediaUrl(s.image)} alt={s.name} onError={handleMediaError} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                ) : (
                  <div className="aspect-video bg-slate-900 grid place-items-center"><Icon className="h-10 w-10 text-amber-400" /></div>
                )}
                <div className="p-4">
                  {s.featured && <span className="text-[10px] font-semibold bg-amber-50 text-amber-700 px-2 py-0.5 rounded">Popular</span>}
                  <h3 className="font-semibold text-slate-900 mt-1">{s.name}</h3>
                  <p className="text-sm text-slate-500 mt-1 line-clamp-2">{s.shortDescription}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-amber-600 mt-3">View details <ArrowRight className="h-4 w-4" /></span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Visit store CTA */}
      <div className="mt-10 rounded-2xl bg-slate-900 text-white p-8 text-center">
        <h2 className="text-2xl font-bold font-display">Visit Our Store</h2>
        <p className="text-slate-300 mt-1">{[b.address, b.city, b.state, b.pincode].filter(Boolean).join(", ")}</p>
        <div className="mt-5 flex flex-wrap gap-3 justify-center">
          <a href={directionsUrl(b)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-amber-500 text-slate-900 font-semibold px-5 py-3 rounded-lg" data-testid="services-directions"><Navigation className="h-4 w-4" /> Get Directions</a>
          {b.phone && <a href={`tel:${b.phone}`} className="inline-flex items-center gap-2 border border-slate-600 font-semibold px-5 py-3 rounded-lg" data-testid="services-call"><Phone className="h-4 w-4" /> Call Now</a>}
          <a href="/contact" className="inline-flex items-center gap-2 border border-slate-600 font-semibold px-5 py-3 rounded-lg"><Mail className="h-4 w-4" /> Contact Us</a>
        </div>
      </div>

      {active && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setActive(null)} />
          <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="service-detail-modal">
            <button onClick={() => setActive(null)} className="absolute top-4 right-4 z-10 p-2 bg-white/90 rounded-full"><X className="h-5 w-5" /></button>
            {active.image && <div className="aspect-video bg-slate-50"><img src={resolveMediaUrl(active.image)} alt={active.name} onError={handleMediaError} className="w-full h-full object-cover" /></div>}
            <div className="p-6">
              <h3 className="text-xl font-bold text-slate-900 font-display">{active.name}</h3>
              <div className="flex flex-wrap gap-3 mt-2 text-sm text-slate-500">
                {active.startingPrice != null && <span className="inline-flex items-center gap-1"><IndianRupee className="h-4 w-4" /> Starting from ₹{active.startingPrice}</span>}
                {active.duration && <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {active.duration}</span>}
              </div>
              <p className="text-slate-600 mt-3">{active.description || active.shortDescription}</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a href={directionsUrl(b)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold px-5 py-2.5 rounded-lg"><Navigation className="h-4 w-4" /> Visit Store</a>
                {b.phone && <a href={`tel:${b.phone}`} className="inline-flex items-center gap-2 border border-slate-200 font-semibold px-5 py-2.5 rounded-lg"><Phone className="h-4 w-4" /> Call</a>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
